import {createSlice} from '@reduxjs/toolkit';
import {displayDateFormat} from 'utils/date-time';
import {getFacilitiesBreakDownData} from './accounting-actions';
import {breakdownDataAdapter} from './accounting-adapters';
import dayjs from 'utils/dayjs';

export const startMonth = dayjs().startOf('month').subtract(1, 'month');

/**
 * @typedef {object} TAccountingreducerInitialState
 * @property {object} filters
 * @property {string} filters.fromDate - The start date for the report in the format MM/DD/YYYY.  Defaults to the start of the previous month
 * @property {string} filters.toDate - The end date for the report in the format MM/DD/YYYY.  Defaults to the end of the previous month
 */
export const initialState = {
    breakdownData: breakdownDataAdapter.getInitialState({
        meta: {
            page: 1,
            perPage: 25,
            pages: 0,
            prevPage: null,
            nextPage: null,
            total: 0,
            hasTrfFacility: false,
        },
    }),
    isLoading: false,
    filters: {
        fromDate: startMonth.format(displayDateFormat),
        toDate: startMonth.endOf('month').format(displayDateFormat),
    },
};

const accountingSlice = createSlice({
    name: 'accounting',
    initialState,
    reducers: {
        /**
         * Updates the filters for the accounting report
         * @memberof Accounting.Actions
         * @param {TAccountingreducerInitialState} [state] - The current state
         * @param payload - The new filters
         * @param payload.fromDate - The start date for the report in the format MM/DD/YYYY.
         * @param payload.toDate - The end date for the report in the format MM/DD/YYYY.
         * @returns {fromDate: string, toDate: string}
         */
        updateFilters: (state, {payload}) => {
            state.filters = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                getFacilitiesBreakDownData.fulfilled,
                (state, {payload}) => {
                    const {data, meta} = payload;

                    breakdownDataAdapter.setAll(
                        state.breakdownData,
                        data.results
                    );

                    state.breakdownData.meta = meta;

                    state.isLoading = false;
                }
            )
            .addCase(getFacilitiesBreakDownData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getFacilitiesBreakDownData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.isLoading = false;
            });
    },
});

export const {updateFilters} = accountingSlice.actions;
export default accountingSlice.reducer;
